import { api } from './api';

interface Theme {
  displayName?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
}

/**
 * White-label do tenant.
 *
 * Carrega o tema de uma destas fontes (nesta ordem):
 *   1. `?slug=<slug>` na URL (deep-link).
 *   2. Subdomínio do host (`minhaclinica.dentalflow.com.br` → `minhaclinica`).
 *   3. Sessão autenticada (`GET /tenants/me/branding`).
 *
 * A clínica configura apenas 2 cores (primária + destaque). A partir
 * delas geramos a escala 50..950 inteira e sobrescrevemos as CSS vars
 * `--c-brand-*` / `--c-accent-*` que o Tailwind consome (ver
 * tailwind.config + index.css). Assim TODO o app — interno e público —
 * passa a usar as cores do tenant, sem rebuild. Sem tema, os defaults
 * teal/cyan de index.css valem.
 */
export async function applyTenantTheme(): Promise<void> {
  const theme = await resolveTheme();
  if (!theme) return;
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (theme.primaryColor) {
    applyScale(root, 'brand', theme.primaryColor, true);
  }
  if (theme.accentColor) {
    applyScale(root, 'accent', theme.accentColor, false);
  }
  if (theme.displayName) {
    document.title = theme.displayName;
  }
}

/**
 * Aplica as cores ao vivo (sem recarregar) — usado pela tela de
 * Aparência logo após salvar, pra o app refletir na hora. `null`/vazio
 * restaura o default removendo as overrides inline.
 */
export function applyThemeColorsLive(
  primaryColor?: string | null,
  accentColor?: string | null,
): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (primaryColor) {
    applyScale(root, 'brand', primaryColor, true);
  } else {
    clearScale(root, 'brand', true);
  }
  if (accentColor) {
    applyScale(root, 'accent', accentColor, false);
  } else {
    clearScale(root, 'accent', false);
  }
}

// ─── Geração de escala de cor ─────────────────────────────────────────

type Rgb = [number, number, number];

/**
 * Quanto misturar a cor base com branco (positivo) ou preto (negativo)
 * pra cada shade. A base é tratada como o shade 600 (cor de ação
 * principal — botões usam bg-brand-600). Valores aproximam a curva de
 * uma paleta Tailwind típica; não é perfeito, mas dá uma escala coerente
 * a partir de uma única cor.
 */
const SHADE_MIX: Record<number, number> = {
  50: 0.95,
  100: 0.88,
  200: 0.74,
  300: 0.56,
  400: 0.33,
  500: 0.14,
  600: 0, // base
  700: -0.14,
  800: -0.28,
  900: -0.42,
  950: -0.56,
};

const BRAND_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
const ACCENT_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

function applyScale(
  root: HTMLElement,
  name: 'brand' | 'accent',
  baseHex: string,
  withScale950: boolean,
): void {
  const base = hexToRgb(baseHex);
  if (!base) return;
  const shades = withScale950 ? BRAND_SHADES : ACCENT_SHADES;
  for (const shade of shades) {
    const ratio = SHADE_MIX[shade] ?? 0;
    const rgb =
      ratio > 0
        ? mix(base, [255, 255, 255], ratio)
        : ratio < 0
          ? mix(base, [0, 0, 0], -ratio)
          : base;
    root.style.setProperty(
      `--c-${name}-${shade}`,
      `${rgb[0]} ${rgb[1]} ${rgb[2]}`,
    );
  }
}

/** Remove as overrides inline de um nome, voltando aos defaults do CSS. */
function clearScale(
  root: HTMLElement,
  name: 'brand' | 'accent',
  withScale950: boolean,
): void {
  const shades = withScale950 ? BRAND_SHADES : ACCENT_SHADES;
  for (const shade of shades) {
    root.style.removeProperty(`--c-${name}-${shade}`);
  }
}

/** Mistura linear de duas cores. ratio=0 → a; ratio=1 → b. */
function mix(a: Rgb, b: Rgb, ratio: number): Rgb {
  const r = Math.max(0, Math.min(1, ratio));
  return [
    Math.round(a[0] + (b[0] - a[0]) * r),
    Math.round(a[1] + (b[1] - a[1]) * r),
    Math.round(a[2] + (b[2] - a[2]) * r),
  ];
}

function hexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const h = m[1];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

// ─── Resolução da fonte do tema ───────────────────────────────────────

/**
 * Subdomínios de sistema que NÃO são clínicas. Em `app.kevins.com.br` o 1º
 * rótulo é `app` — sem isto o front pedia /public/branding/app e tomava 404
 * (inofensivo, mas poluía o console a cada load). Clínicas white-label usam
 * subdomínios próprios (ex.: `clinicax.kevins.com.br`).
 */
export const RESERVED_SUBDOMAINS = new Set([
  'app',
  'www',
  'api',
  'admin',
  'portal',
  'staging',
  'dev',
  'test',
]);

async function resolveTheme(): Promise<Theme | null> {
  // 1. `?slug=` explicit.
  const params =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null;
  const slugParam = params?.get('slug');
  if (slugParam) {
    return fetchBySlug(slugParam);
  }
  // 2. Subdomínio.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const parts = host.split('.');
  if (
    parts.length >= 3 &&
    !host.startsWith('localhost') &&
    !host.startsWith('127.') &&
    !RESERVED_SUBDOMAINS.has(parts[0].toLowerCase())
  ) {
    const slug = parts[0];
    const theme = await fetchBySlug(slug);
    if (theme) return theme;
  }
  // 3. Auth: só tenta se o usuário está logado.
  if (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('dentalflow.access')
  ) {
    try {
      const res = await api.get('/tenants/me/branding');
      return (res.data?.themeJson as Theme) ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

async function fetchBySlug(slug: string): Promise<Theme | null> {
  try {
    const res = await api.get(`/public/branding/${encodeURIComponent(slug)}`);
    return (res.data?.theme as Theme) ?? null;
  } catch {
    return null;
  }
}
